/**
 * 메뉴 설정 관리 — API 트리 ↔ 화면 flat MenuTreeItem 변환.
 * DnD 유틸은 string id flat 트리를 쓰므로 API number id를 문자열로 맞춘다.
 */
import type {
  AdminMenuAccessScope,
  AdminMenuTreeNode,
  AdminMenuTreeReorderRequest,
  AdminMenuUpsertRequest,
} from '@/entities/menu/types';
import {
  DEFAULT_MENU_ICON_KEY,
  type MenuAudience,
  type MenuTreeItem,
} from './settingsMenuData';

export const accessScopeToAudience = (
  scope: AdminMenuAccessScope,
): MenuAudience => (scope === 'ADMIN_ONLY' ? 'admin' : 'general');

export const audienceToAccessScope = (
  audience: MenuAudience,
): AdminMenuAccessScope =>
  audience === 'admin' ? 'ADMIN_ONLY' : 'GENERAL';

/** nested API 트리 → DnD용 flat 목록 (형제 순서 = 배열 순서) */
export const flattenAdminMenuTree = (
  nodes: AdminMenuTreeNode[],
): MenuTreeItem[] => {
  const result: MenuTreeItem[] = [];

  const walk = (list: AdminMenuTreeNode[], inferredParentId: string | null) => {
    for (const node of list) {
      const parentId =
        node.parentId != null ? String(node.parentId) : inferredParentId;
      result.push({
        id: String(node.id),
        name: node.menuName,
        menuKey: node.menuKey,
        url: node.urlPath ?? '',
        parentId,
        visible: node.visible,
        audience: accessScopeToAudience(node.accessScope),
        iconKey: DEFAULT_MENU_ICON_KEY,
        sectionLabel: '메뉴',
        systemDefined: false,
        hasChildrenFlag: (node.children?.length ?? 0) > 0,
      });
      if (node.children?.length) {
        walk(node.children, String(node.id));
      }
    }
  };

  walk(nodes, null);
  return result;
};

/**
 * flat 트리 → PUT /admin/menus/tree 페이로드.
 * 모든 메뉴 ID를 포함해야 하며, 최대 2단계(루트 + 직계 자식)만 허용한다.
 */
export const toMenuTreeReorderRequest = (
  items: MenuTreeItem[],
): AdminMenuTreeReorderRequest => {
  const roots = items.filter((item) => item.parentId === null);
  return {
    menus: roots.map((root) => ({
      id: Number(root.id),
      children: items
        .filter((child) => child.parentId === root.id)
        .map((child) => ({
          id: Number(child.id),
          children: [],
        })),
    })),
  };
};

/** 계층·순서 비교용 시그니처 — 메타 변경은 트리 dirty에 넣지 않는다 */
export const menuTreeStructureSignature = (items: MenuTreeItem[]): string =>
  items.map((item) => `${item.id}:${item.parentId ?? ''}`).join('|');

export const toMenuUpsertRequest = (input: {
  name: string;
  parentId: string | null;
  url: string;
  visible: boolean;
  audience: MenuAudience;
}): AdminMenuUpsertRequest => {
  const trimmedUrl = input.url.trim();
  return {
    menuName: input.name.trim(),
    parentId: input.parentId != null ? Number(input.parentId) : null,
    urlPath: trimmedUrl.length > 0 ? trimmedUrl : null,
    visible: input.visible,
    accessScope: audienceToAccessScope(input.audience),
  };
};

/**
 * PUT 응답에 메타가 빠질 수 있어, 기존 flat과 id 기준으로 병합한다.
 * 응답에 menuName 등이 있으면 응답을 우선한다.
 */
export const mergeTreeResponseWithLocal = (
  responseMenus: AdminMenuTreeNode[],
  previous: MenuTreeItem[],
): MenuTreeItem[] => {
  const hasMeta = responseMenus.some((n) => Boolean(n.menuKey || n.menuName));
  if (hasMeta) {
    return flattenAdminMenuTree(responseMenus);
  }

  const byId = new Map(previous.map((item) => [item.id, item]));
  const next: MenuTreeItem[] = [];

  const walk = (
    nodes: Array<{
      id: number;
      children?: Array<{ id: number; children?: unknown[] }>;
    }>,
    parentId: string | null,
  ) => {
    for (const node of nodes) {
      const id = String(node.id);
      const prev = byId.get(id);
      if (prev) {
        next.push({ ...prev, parentId });
      }
      if (node.children?.length) {
        walk(node.children as typeof nodes, id);
      }
    }
  };

  walk(responseMenus, null);
  return next.length > 0 ? next : previous;
};
